import React, { useState, useEffect, useRef } from 'react';
import { Bell, CalendarDays, Euro, CheckCheck, X, AlertTriangle, GitMerge, SplitSquareHorizontal, ArrowLeftRight, FileWarning, RefreshCw, MessageCircle, UserPlus, Car, Users, Contact, FileText } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NotificationBell = ({ onArticleClick }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isPartner = user?.role === 'partner' || user?.role === 'partenaire';
  const [articles, setArticles] = useState([]);
  const [demandeNotifs, setDemandeNotifs] = useState([]);
  const [duplicateAlerts, setDuplicateAlerts] = useState([]);
  const [docNotifs, setDocNotifs] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const fetchNotifications = async () => {
    try {
      setLoading(true);

      // ── Réinitialisation stricte par rôle ──────────────────────────────────
      // Les alertes doublons sont EXCLUSIVEMENT admin
      if (!isAdmin) setDuplicateAlerts([]);
      // Les demandes RH (acompte, congé, voiture, réattribution) sont EXCLUSIVEMENT admin
      // Les partenaires ont leurs propres notifs réclamations ILEK via le même endpoint
      if (!isAdmin && !isPartner) setDemandeNotifs([]);

      // Articles et documents RH visibles par tous
      const promises = [
        axios.get(`${API_URL}/articles/notifications`),
        axios.get(`${API_URL}/documents-rh/notifications`),
      ];
      // Demandes et doublons : admins uniquement
      if (isAdmin) {
        promises.push(
          axios.get(`${API_URL}/demandes/notifications`),
          axios.get(`${API_URL}/contracts/duplicate-alerts`)
        );
      }
      // Notifications réclamations ILEK : partenaires uniquement
      if (isPartner) {
        promises.push(axios.get(`${API_URL}/demandes/notifications`));
      }
      const results = await Promise.allSettled(promises);
      if (results[0]?.status === 'fulfilled') setArticles(results[0].value.data.notifications || []);
      if (results[1]?.status === 'fulfilled') setDocNotifs(results[1].value.data.notifications || []);
      if (isAdmin) {
        if (results[2]?.status === 'fulfilled') setDemandeNotifs(results[2].value.data.notifications || []);
        if (results[3]?.status === 'fulfilled') setDuplicateAlerts(results[3].value.data.alerts || []);
      }
      if (isPartner) {
        const partnerNotifsResult = results[2];
        if (partnerNotifsResult?.status === 'fulfilled') setDemandeNotifs(partnerNotifsResult.value.data.notifications || []);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    // Réinitialisation immédiate des états sensibles lors d'un changement de rôle
    // → évite que les notifications admin restent visibles après impersonation
    if (!isAdmin) setDuplicateAlerts([]);
    if (!isAdmin && !isPartner) setDemandeNotifs([]);
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user?.role]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    if (showDropdown) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  const headers = () => ({});

  // ── Marquer une demande comme lue ───────────────────────────────────────
  const markDemandeRead = async (notifId, e) => {
    e?.stopPropagation();
    setDemandeNotifs(prev => prev.filter(n => n.id !== notifId));
    try {
      await axios.post(`${API_URL}/demandes/notifications/${notifId}/read`, {}, { headers: headers() });
    } catch { /* silencieux */ }
  };

  // ── Marquer un article comme lu ─────────────────────────────────────────
  const markArticleRead = async (articleId, e) => {
    e?.stopPropagation();
    setArticles(prev => prev.filter(a => a.id !== articleId));
    try {
      await axios.post(`${API_URL}/articles/${articleId}/mark-read`, {}, { headers: headers() });
    } catch { /* silencieux */ }
  };

  // ── Marquer une notification de document RH comme lue ──────────────────
  const markDocNotifRead = async (notifId, e) => {
    e?.stopPropagation();
    setDocNotifs(prev => prev.filter(n => n.id !== notifId));
    try {
      await axios.post(`${API_URL}/documents-rh/notifications/${notifId}/read`, {}, { headers: headers() });
    } catch { /* silencieux */ }
  };

  // ── Tout marquer comme lu ────────────────────────────────────────────────
  const markAllRead = async () => {
    setMarkingAll(true);
    const demandesToMark = [...demandeNotifs];
    const articlesToMark = [...articles];
    const hasDocNotifs = docNotifs.length > 0;
    setDemandeNotifs([]);
    setArticles([]);
    setDocNotifs([]);
    try {
      await Promise.allSettled([
        axios.post(`${API_URL}/demandes/notifications/read-all`, {}, { headers: headers() }),
        hasDocNotifs ? axios.post(`${API_URL}/documents-rh/notifications/read-all`, {}, { headers: headers() }) : Promise.resolve(),
        ...articlesToMark.map(a => axios.post(`${API_URL}/articles/${a.id}/mark-read`, {}, { headers: headers() })),
      ]);
    } catch { /* silencieux */ }
    setMarkingAll(false);
  };

  // ── Fusionner un doublon ────────────────────────────────────────────────
  const handleMergeDuplicate = async (alertId, e) => {
    e?.stopPropagation();
    try {
      await axios.post(`${API_URL}/contracts/duplicate-alerts/${alertId}/merge`, {}, { headers: headers() });
      setDuplicateAlerts(prev => prev.filter(a => a.alert_id !== alertId));
      toast.success('Contrats fusionnés');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la fusion');
    }
  };

  // ── Ignorer un doublon ──────────────────────────────────────────────────
  const handleDismissDuplicate = async (alertId, e) => {
    e?.stopPropagation();
    try {
      await axios.post(`${API_URL}/contracts/duplicate-alerts/${alertId}/dismiss`, {}, { headers: headers() });
      setDuplicateAlerts(prev => prev.filter(a => a.alert_id !== alertId));
      toast.success('Alerte ignorée');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleArticleClick = (article) => {
    setShowDropdown(false);
    onArticleClick(article);
  };

  const handleDemandeNotifClick = async (notif) => {
    await markDemandeRead(notif.id);
    setShowDropdown(false);
    navigate('/suivi-rh?tab=demandes');
  };

  const handleOnboardingNotifClick = async (notif) => {
    await markDemandeRead(notif.id);
    setShowDropdown(false);
    navigate('/suivi-rh');
  };

  const handleVehicleNotifClick = async (notif) => {
    await markDemandeRead(notif.id);
    setShowDropdown(false);
    navigate('/voitures');
  };

  const handleReclamationNotifClick = async (notif) => {
    await markDemandeRead(notif.id);
    setShowDropdown(false);
    navigate('/ilek/qualite');
  };

  const handleDocNotifClick = async (notif) => {
    await markDocNotifRead(notif.id);
    setShowDropdown(false);
    navigate('/mes-documents');
  };

  // Séparer les notifs partenaire (réclamations) des notifs admin (demandes RH) et onboarding
  const reclamationNotifs = demandeNotifs.filter(n =>
    ['nouvelle_reclamation', 'statut_reclamation', 'commentaire_reclamation'].includes(n.type)
  );
  const onboardingNotifs = demandeNotifs.filter(n => n.type === 'onboarding_submission');
  const vehicleNotifs = demandeNotifs.filter(n => n.type === 'observation_vehicule');
  const ctNotifs = demandeNotifs.filter(n => n.type === 'ct_alerte');
  const rhAlerteNotifs = demandeNotifs.filter(n => n.type === 'rh_alerte');
  const recrutementNotifs = demandeNotifs.filter(n => n.type === 'recrutement_candidat');
  const ilekMissingNotifs = demandeNotifs.filter(n => ['ilek_contrats_absents', 'ilek_valeurs_protegees', 'ilek_colonnes_modifiees'].includes(n.type));
  const rhNotifs = demandeNotifs.filter(n =>
    !['nouvelle_reclamation', 'statut_reclamation', 'commentaire_reclamation', 'onboarding_submission', 'observation_vehicule', 'ct_alerte', 'rh_alerte', 'recrutement_candidat', 'ilek_contrats_absents', 'ilek_valeurs_protegees', 'ilek_colonnes_modifiees'].includes(n.type)
  );

  // ── Naviguer vers la comparaison de doublons ─────────────────────────────
  const handleCompareDuplicate = (alert) => {
    setShowDropdown(false);
    const params = new URLSearchParams({
      compare: `${alert.manual_account_number},${alert.imported_account_number}`,
      alert_id: alert.alert_id,
    });
    navigate(`/contracts?${params.toString()}`);
  };

  const totalUnread = articles.length + demandeNotifs.length + duplicateAlerts.length + docNotifs.length;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative p-2 text-muted-foreground hover:text-foreground focus:outline-none transition-colors"
        aria-label="Notifications"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5" />
        {totalUnread > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center w-4 h-4 text-xs font-bold text-white bg-red-500 rounded-full">
            {totalUnread > 9 ? '9+' : totalUnread}
          </span>
        )}
      </button>

      {showDropdown && (
        <div className="absolute right-0 lg:left-0 lg:right-auto mt-2 w-80 bg-card rounded-lg shadow-xl border z-50">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b">
            <div>
              <h3 className="font-semibold text-sm">Notifications</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                {totalUnread} non {totalUnread <= 1 ? 'lue' : 'lues'}
              </p>
            </div>
            {totalUnread > 0 && (
              <button
                onClick={markAllRead}
                disabled={markingAll}
                data-testid="mark-all-read-btn"
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium
                  text-muted-foreground hover:text-foreground hover:bg-muted/60
                  disabled:opacity-50 transition-colors"
                title="Tout marquer comme lu"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                Tout lire
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto overflow-x-hidden">
            {loading ? (
              <div className="p-4 text-center text-sm text-muted-foreground">Chargement...</div>
            ) : totalUnread === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">Aucune notification</div>
            ) : (
              <div className="divide-y">
                {/* Alertes doublons PDL/PCE */}
                {duplicateAlerts.map((alert) => (
                  <div key={alert.alert_id} className="p-3 hover:bg-muted/50 transition-colors" data-testid={`duplicate-alert-${alert.alert_id}`}>
                    <div className="flex items-start gap-2">
                      <div className="mt-0.5 p-1 rounded-full bg-amber-100 dark:bg-amber-900/30 shrink-0">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium leading-snug">
                          Doublon {alert.match_type} détecté
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Manuel <span className="font-mono font-semibold text-sky-600">{alert.manual_account_number}</span> ({alert.manual_contract?.family_name} {alert.manual_contract?.given_name})
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Importé <span className="font-mono font-semibold">{alert.imported_account_number}</span> ({alert.imported_contract?.family_name} {alert.imported_contract?.given_name})
                        </p>
                        <div className="flex gap-1.5 mt-2 flex-wrap">
                          {/* Bouton principal : Comparer côte-à-côte */}
                          <button
                            onClick={() => handleCompareDuplicate(alert)}
                            data-testid={`compare-btn-${alert.alert_id}`}
                            className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-amber-100 text-amber-800 hover:bg-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:hover:bg-amber-900/60 transition-colors"
                          >
                            <ArrowLeftRight className="w-3 h-3" />
                            Comparer
                          </button>
                          <button
                            onClick={(e) => handleMergeDuplicate(alert.alert_id, e)}
                            data-testid={`merge-btn-${alert.alert_id}`}
                            className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/50 transition-colors"
                          >
                            <GitMerge className="w-3 h-3" />
                            Fusionner
                          </button>
                          <button
                            onClick={(e) => handleDismissDuplicate(alert.alert_id, e)}
                            data-testid={`dismiss-btn-${alert.alert_id}`}
                            className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 transition-colors"
                          >
                            <SplitSquareHorizontal className="w-3 h-3" />
                            Séparer
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Notifications documents RH (BS/RUN) — tous rôles */}
                {docNotifs.map((notif) => (
                  <div key={notif.id} className="flex items-start hover:bg-muted/50 transition-colors">
                    <button
                      onClick={() => handleDocNotifClick(notif)}
                      className="flex-1 p-3 text-left min-w-0"
                      data-testid={`doc-notif-${notif.id}`}
                    >
                      <div className="flex items-start gap-2">
                        <div className="mt-1 p-1 rounded-full shrink-0 bg-violet-100 dark:bg-violet-900/30">
                          <FileText className="w-3 h-3 text-violet-600 dark:text-violet-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-violet-600 dark:text-violet-400 uppercase tracking-wide mb-0.5">Document RH</p>
                          <p className="text-sm font-medium leading-snug break-words">{notif.message}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {new Date(notif.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).replace(/\//g, '-')}
                          </p>
                        </div>
                      </div>
                    </button>
                    <button
                      onClick={(e) => markDocNotifRead(notif.id, e)}
                      data-testid={`mark-read-doc-${notif.id}`}
                      className="self-start mt-2 mr-2 p-1.5 rounded-md shrink-0 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      title="Marquer comme lu"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}

                {/* Notifications demandes RH (admin uniquement) */}
                {rhNotifs.map((notif) => (
                  <div key={notif.id} className="flex items-start hover:bg-muted/50 transition-colors">
                    <button
                      onClick={() => handleDemandeNotifClick(notif)}
                      className="flex-1 p-3 text-left min-w-0"
                    >
                      <div className="flex items-start gap-2">
                        <div className={`mt-1 p-1 rounded-full shrink-0 ${notif.type === 'nouvelle_demande' ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-green-100 dark:bg-green-900/30'}`}>
                          {notif.type === 'nouvelle_demande'
                            ? <CalendarDays className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                            : <Euro className="w-3 h-3 text-green-600 dark:text-green-400" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium leading-snug break-words">{notif.message}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {new Date(notif.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).replace(/\//g, '-')}
                          </p>
                        </div>
                      </div>
                    </button>
                    <button
                      onClick={(e) => markDemandeRead(notif.id, e)}
                      data-testid={`mark-read-demande-${notif.id}`}
                      className="self-start mt-2 mr-2 p-1.5 rounded-md shrink-0 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      title="Marquer comme lu"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}

                {/* Notifications Onboarding (admin) */}
                {onboardingNotifs.map((notif) => (
                  <div key={notif.id} className="flex items-start hover:bg-muted/50 transition-colors">
                    <button
                      onClick={() => handleOnboardingNotifClick(notif)}
                      className="flex-1 p-3 text-left min-w-0"
                    >
                      <div className="flex items-start gap-2">
                        <div className="mt-1 p-1 rounded-full shrink-0 bg-orange-100 dark:bg-orange-900/30">
                          <UserPlus className="w-3 h-3 text-orange-600 dark:text-orange-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-orange-600 dark:text-orange-400 uppercase tracking-wide mb-0.5">Onboarding</p>
                          <p className="text-sm font-medium leading-snug break-words">{notif.message}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {new Date(notif.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).replace(/\//g, '-')}
                          </p>
                        </div>
                      </div>
                    </button>
                    <button
                      onClick={(e) => markDemandeRead(notif.id, e)}
                      className="self-start mt-2 mr-2 p-1.5 rounded-md shrink-0 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      title="Marquer comme lu"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}

                {/* Notifications véhicules (note observations) */}
                {vehicleNotifs.map((notif) => (
                  <div key={notif.id} className="flex items-start hover:bg-muted/50 transition-colors">
                    <button
                      onClick={() => handleVehicleNotifClick(notif)}
                      className="flex-1 p-3 text-left min-w-0"
                      data-testid={`vehicle-notif-${notif.id}`}
                    >
                      <div className="flex items-start gap-2">
                        <div className="mt-1 p-1 rounded-full shrink-0 bg-sky-100 dark:bg-sky-900/30">
                          <Car className="w-3 h-3 text-sky-600 dark:text-sky-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-sky-600 dark:text-sky-400 uppercase tracking-wide mb-0.5">Véhicule</p>
                          <p className="text-sm font-medium leading-snug break-words">{notif.message}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {new Date(notif.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).replace(/\//g, '-')}
                          </p>
                        </div>
                      </div>
                    </button>
                    <button
                      onClick={(e) => markDemandeRead(notif.id, e)}
                      className="self-start mt-2 mr-2 p-1.5 rounded-md shrink-0 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      title="Marquer comme lu"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}

                {/* Alertes contrats ILEK absents de l'export */}
                {ilekMissingNotifs.map((notif) => (
                  <div key={notif.id} className="flex items-start hover:bg-muted/50 transition-colors">
                    <button
                      onClick={async () => { await markDemandeRead(notif.id); setShowDropdown(false); if (notif.type !== 'ilek_colonnes_modifiees') navigate(`/ilek/contracts?presence=${notif.type === 'ilek_valeurs_protegees' ? 'protected' : 'missing'}`); }}
                      className="flex-1 p-3 text-left min-w-0"
                      data-testid={`ilek-missing-notif-${notif.id}`}
                    >
                      <div className="flex items-start gap-2">
                        <div className={`mt-1 p-1 rounded-full shrink-0 ${notif.type === 'ilek_valeurs_protegees' ? 'bg-sky-100 dark:bg-sky-900/30' : 'bg-orange-100 dark:bg-orange-900/30'}`}>
                          <AlertTriangle className={`w-3 h-3 ${notif.type === 'ilek_valeurs_protegees' ? 'text-sky-600 dark:text-sky-400' : 'text-orange-600 dark:text-orange-400'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-xs font-semibold uppercase tracking-wide mb-0.5 ${notif.type === 'ilek_valeurs_protegees' ? 'text-sky-600 dark:text-sky-400' : 'text-orange-600 dark:text-orange-400'}`}>
                            {notif.type === 'ilek_valeurs_protegees' ? 'Export ILEK — écrasement évité' : notif.type === 'ilek_colonnes_modifiees' ? 'Export ILEK — colonnes modifiées' : 'Export ILEK'}
                          </p>
                          <p className="text-sm font-medium leading-snug break-words">{notif.message}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {new Date(notif.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).replace(/\//g, '-')}
                          </p>
                        </div>
                      </div>
                    </button>
                    <button
                      onClick={(e) => markDemandeRead(notif.id, e)}
                      className="self-start mt-2 mr-2 p-1.5 rounded-md shrink-0 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      title="Marquer comme lu"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}

                {/* Alertes Contrôle Technique */}
                {ctNotifs.map((notif) => (
                  <div key={notif.id} className="flex items-start hover:bg-muted/50 transition-colors">
                    <button
                      onClick={async () => { await markDemandeRead(notif.id); setShowDropdown(false); navigate('/voitures'); }}
                      className="flex-1 p-3 text-left min-w-0"
                      data-testid={`ct-notif-${notif.id}`}
                    >
                      <div className="flex items-start gap-2">
                        <div className="mt-1 p-1 rounded-full shrink-0 bg-red-100 dark:bg-red-900/30">
                          <AlertTriangle className="w-3 h-3 text-red-600 dark:text-red-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-red-600 dark:text-red-400 uppercase tracking-wide mb-0.5">Alerte CT</p>
                          <p className="text-sm font-medium leading-snug break-words">{notif.message}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {new Date(notif.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).replace(/\//g, '-')}
                          </p>
                        </div>
                      </div>
                    </button>
                    <button
                      onClick={(e) => markDemandeRead(notif.id, e)}
                      className="self-start mt-2 mr-2 p-1.5 rounded-md shrink-0 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      title="Marquer comme lu"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}

                {/* Alertes RH Personnel (DPAE, onboarding en retard) */}
                {rhAlerteNotifs.map((notif) => (
                  <div key={notif.id} className="flex items-start hover:bg-muted/50 transition-colors">
                    <button
                      onClick={async () => { await markDemandeRead(notif.id); setShowDropdown(false); navigate('/suivi-cartons?tab=dashboard-rh'); }}
                      className="flex-1 p-3 text-left min-w-0"
                      data-testid={`rh-alerte-notif-${notif.id}`}
                    >
                      <div className="flex items-start gap-2">
                        <div className="mt-1 p-1 rounded-full shrink-0 bg-amber-100 dark:bg-amber-900/30">
                          <Users className="w-3 h-3 text-amber-600 dark:text-amber-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wide mb-0.5">Alerte RH</p>
                          <p className="text-sm font-medium leading-snug break-words">{notif.message}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {new Date(notif.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).replace(/\//g, '-')}
                          </p>
                        </div>
                      </div>
                    </button>
                    <button
                      onClick={(e) => markDemandeRead(notif.id, e)}
                      className="self-start mt-2 mr-2 p-1.5 rounded-md shrink-0 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      title="Marquer comme lu"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}

                {/* Notifications Recrutement (candidat assigné) */}
                {recrutementNotifs.map((notif) => (
                  <div key={notif.id} className="flex items-start hover:bg-muted/50 transition-colors">
                    <button
                      onClick={async () => { await markDemandeRead(notif.id); setShowDropdown(false); navigate('/suivi-cartons?tab=recrutement'); }}
                      className="flex-1 p-3 text-left min-w-0"
                      data-testid={`recrutement-notif-${notif.id}`}
                    >
                      <div className="flex items-start gap-2">
                        <div className="mt-1 p-1 rounded-full shrink-0 bg-teal-100 dark:bg-teal-900/30">
                          <Contact className="w-3 h-3 text-teal-600 dark:text-teal-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-teal-600 dark:text-teal-400 uppercase tracking-wide mb-0.5">Recrutement</p>
                          <p className="text-sm font-medium leading-snug break-words">{notif.message}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {new Date(notif.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).replace(/\//g, '-')}
                          </p>
                        </div>
                      </div>
                    </button>
                    <button
                      onClick={(e) => markDemandeRead(notif.id, e)}
                      className="self-start mt-2 mr-2 p-1.5 rounded-md shrink-0 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      title="Marquer comme lu"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}

                {/* Notifications réclamations ILEK (partenaires ILEK) */}
                {reclamationNotifs.map((notif) => {
                  const iconMap = {
                    nouvelle_reclamation: { icon: FileWarning, bg: 'bg-orange-100 dark:bg-orange-900/30', color: 'text-orange-600 dark:text-orange-400' },
                    statut_reclamation:   { icon: RefreshCw,   bg: 'bg-blue-100 dark:bg-blue-900/30',   color: 'text-blue-600 dark:text-blue-400'   },
                    commentaire_reclamation: { icon: MessageCircle, bg: 'bg-emerald-100 dark:bg-emerald-900/30', color: 'text-emerald-600 dark:text-emerald-400' },
                  };
                  const cfg = iconMap[notif.type] || iconMap.nouvelle_reclamation;
                  const Icon = cfg.icon;
                  return (
                    <div key={notif.id} className="flex items-start hover:bg-muted/50 transition-colors">
                      <button
                        onClick={() => handleReclamationNotifClick(notif)}
                        className="flex-1 p-3 text-left min-w-0"
                      >
                        <div className="flex items-start gap-2">
                          <div className={`mt-1 p-1 rounded-full shrink-0 ${cfg.bg}`}>
                            <Icon className={`w-3 h-3 ${cfg.color}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wide mb-0.5">ILEK — Réclamation</p>
                            <p className="text-sm font-medium leading-snug break-words">{notif.message}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {new Date(notif.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).replace(/\//g, '-')}
                            </p>
                          </div>
                        </div>
                      </button>
                      <button
                        onClick={(e) => markDemandeRead(notif.id, e)}
                        data-testid={`mark-read-reclamation-${notif.id}`}
                        className="self-start mt-2 mr-2 p-1.5 rounded-md shrink-0 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                        title="Marquer comme lu"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  );
                })}

                {/* Notifications articles */}
                {articles.map((article) => (
                  <div key={article.id} className="flex items-start hover:bg-muted/50 transition-colors">
                    <button
                      onClick={() => handleArticleClick(article)}
                      className="flex-1 p-3 text-left min-w-0"
                    >
                      <div className="flex items-start gap-2">
                        <span className={`inline-block w-2 h-2 mt-1.5 rounded-full shrink-0 ${article.notification_status === 'unread' ? 'bg-blue-500' : 'bg-orange-500'}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium leading-snug break-words">{article.title}</p>
                          <p className="text-xs text-muted-foreground mt-0.5 break-words">{article.content}</p>
                          {article.notification_status === 'remind_later' && (
                            <p className="text-xs text-orange-500 mt-0.5">À rappeler</p>
                          )}
                        </div>
                      </div>
                    </button>
                    <button
                      onClick={(e) => markArticleRead(article.id, e)}
                      data-testid={`mark-read-article-${article.id}`}
                      className="self-start mt-2 mr-2 p-1.5 rounded-md shrink-0 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      title="Marquer comme lu"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {totalUnread > 0 && (
            <div className="p-2 border-t">
              <Button variant="ghost" size="sm" className="w-full text-xs"
                onClick={() => setShowDropdown(false)}>
                Fermer
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
